import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { type TabOption } from '@/components/chariot-ui';
import { Checkbox } from '@/components/form/Checkbox';
import { Modal } from '@/components/Modal';
import Tabs from '@/components/Tabs';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';

import { DeploymentTab } from './DeploymentTab';
import { DownloadAccordion } from './DownloadAccordion';
import { PrerequisitesAccordion } from './PrerequisitesAccordion';

interface AgentSetupModalProps {
  open: boolean;
  onClose: () => void;
  onDownloadInstaller: (
    type: 'msi' | 'deb' | 'rpm',
    proxyConfiguration?: string
  ) => Promise<void>;
}

/**
 * AgentSetupModal orchestrates the multi-step agent setup workflow.
 *
 * Extracted from agents/index.tsx Phase 1.1 refactor (2025-10-22)
 *
 * Features:
 * - Multi-tab setup flow (Prerequisites � Download � Deployment)
 * - Acknowledgment checkbox before proceeding to download
 * - Next/Cancel navigation with proper state management
 * - Automatic state reset on close
 */
export const AgentSetupModal = ({
  open,
  onClose,
  onDownloadInstaller,
}: AgentSetupModalProps) => {
  const [selectedTab, setSelectedTab] = useState<TabOption>({
    value: 'prerequisites',
    label: 'Prerequisites',
  });
  const [hasReadInstructions, setHasReadInstructions] =
    useState<boolean>(false);

  const agentConfigTabs: TabOption[] = [
    { value: 'prerequisites', label: 'Prerequisites' },
    { value: 'download', label: 'Endpoint Download' },
    { value: 'deployment', label: 'Deployment' },
  ];

  const handleClose = () => {
    onClose();
    setHasReadInstructions(false);
    setSelectedTab({
      value: 'prerequisites',
      label: 'Prerequisites',
    });
  };

  const handleNextTab = () => {
    if (selectedTab.value === 'prerequisites') {
      setSelectedTab({ value: 'download', label: 'Download' });
    } else if (selectedTab.value === 'download') {
      setSelectedTab({ value: 'deployment', label: 'Deployment' });
    } else {
      // Reset the modal and tabs
      handleClose();
    }
  };

  const getNextButtonLabel = () => {
    if (selectedTab.value === 'prerequisites') {
      return 'Next: Download Endpoint';
    } else if (selectedTab.value === 'download') {
      return 'Next: Deployment';
    } else {
      return 'Done';
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Set Up Aegis Endpoint"
      size="5xl"
      hideFooter
    >
      <div className="flex flex-col gap-4">
        <div className="border-chariot-stroke mb-2 flex flex-row gap-1 rounded-lg border p-4">
          <Tabs
            id="agent-setup-tabs"
            variant="tertiary"
            tabs={agentConfigTabs}
            selected={selectedTab}
            onSelect={(tab: TabOption) => setSelectedTab(tab)}
          />
        </div>

        <div className="max-h-[600px] overflow-y-auto">
          <div className="px-2">
            {selectedTab.value === 'prerequisites' && (
              <PrerequisitesAccordion />
            )}

            {selectedTab.value === 'download' && (
              <DownloadAccordion onDownloadInstaller={onDownloadInstaller} />
            )}

            {selectedTab.value === 'deployment' && <DeploymentTab />}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between pt-4">
          {selectedTab.value === 'prerequisites' ? (
            <Checkbox
              name="hasReadInstructions"
              label="I have read this and am ready to download the endpoint installer"
              value={hasReadInstructions ? 'true' : 'false'}
              className="text-chariot-text-secondary"
              onChange={e => {
                setHasReadInstructions(!!e.target.value);
              }}
            />
          ) : (
            <div></div>
          )}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Tooltip
              title={
                !hasReadInstructions && selectedTab.value === 'prerequisites'
                  ? 'Please check the acknowledgment box to continue'
                  : ''
              }
            >
              <Button
                size="sm"
                disabled={
                  !hasReadInstructions && selectedTab.value === 'prerequisites'
                }
                onClick={handleNextTab}
              >
                {getNextButtonLabel()}
                {selectedTab.value !== 'deployment' && (
                  <ChevronRightIcon className="size-4" />
                )}
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
    </Modal>
  );
};
